<template>
  <div v-if="suggestions.length" class="date-suggestions">
    <div class="date-suggestions--heading">Vola in una data vicina</div>
    <div class="content-panel panel date-suggestions--panel">
      <swiper
        ref_id="date-suggestions"
        :swiper_params="swiper_params"
        @swiper-initialized="scroll_to_selected"
      >
        <swiper-slide
          v-for="(suggestion, index) in suggestions"
          :key="'date_' + suggestion.in + suggestion.out + _uid"
        >
          <button
            type="button"
            class="date-suggestions--date"
            :class="{
              'date-suggestions--date--selected': selected_index === index,
            }"
            @click="search_by_dates(suggestion)"
          >
            <span class="date-suggestions--range">
              {{ format_date_range(suggestion.in, suggestion.out) }}
            </span>
            <span class="date-suggestions--price">
              a partire da
              <span>{{
                      apply_markup_to_number(
                        suggestion.flight_offer,
                        suggestion.min_price
                      )
                    }}
                {{ suggestion.currencySymbol || suggestion.currency }}</span>
            </span>
          </button>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
  import { apply_markup_to_number } from '@liveflight/utilities/price';
  import { format_date_range } from '@liveflight/utilities/dates';
  import {
    build_date_suggestions,
    find_selected_date_suggestion,
  } from '@liveflight/utilities/date-suggestions';
  import { update_origin_destination_by_index } from '@liveflight/utilities/origin-destination';
  import Swiper from '@/components/blocks/swiper.vue';
  import { md, lg, xl } from '@running_app_src/scss/abstract/_custom.scss';

  export default {
    name: 'date-suggestions',
    components: {
      swiper: Swiper,
    },
    data() {
      return {
        swiper_params: {
          slidesPerView: 2,
          spaceBetween: 0,
          loop: false,
          // Adds `swiper-slide-visible`, used to hide the leftmost separator
          watchSlidesProgress: true,
          button_offset: 38,
          controls_size: 12,
          hide_controls_on_lock: true,
          breakpoints: {
            [parseInt(md, 10)]: { slidesPerView: 3 },
            [parseInt(lg, 10)]: { slidesPerView: 4 },
            [parseInt(xl, 10)]: { slidesPerView: 5 },
          },
        },
      };
    },
    computed: {
      return_flight() {
        return this.$store.state.liveflight.return_flight;
      },
      origin_destinations() {
        return this.$store.state.liveflight.origin_destinations;
      },
      suggestions() {
        return build_date_suggestions(
          this.$store.state.liveflight.date_suggestions,
          this.return_flight
        );
      },
      selected_index() {
        return find_selected_date_suggestion(
          this.suggestions,
          this.$store.state.liveflight.search_request
        );
      },
    },
    created() {
      this.format_date_range = format_date_range;
      this.apply_markup_to_number = apply_markup_to_number;
    },
    methods: {
      scroll_to_selected(swiper_element) {
        const { swiper } = swiper_element || {};
        if (!swiper || this.selected_index < 0) {
          return;
        }
        const per_view =
          typeof swiper.slidesPerViewDynamic === 'function'
            ? swiper.slidesPerViewDynamic()
            : Number(swiper.params.slidesPerView) || 1;
        const first_visible = swiper.activeIndex;
        if (
          this.selected_index < first_visible ||
          this.selected_index >= first_visible + per_view
        ) {
          swiper.slideTo(this.selected_index);
        }
      },
      search_by_dates(suggestion) {
        const updated = update_origin_destination_by_index(
          this.origin_destinations,
          0,
          { date: suggestion.in },
          this.return_flight
        );

        if (suggestion.out && updated[1]) {
          updated[1] = { ...updated[1], date: suggestion.out };
        }

        const { date_suggestions } = this.$store.state.liveflight;
        this.$store.commit('liveflight/origin_destinations', updated);
        window.eventBus.$emit('trigger-search-by-suggestion');
        this.$store.commit('liveflight/date_suggestions', date_suggestions);
      },
    },
  };
</script>

<style lang="scss">
  .date-suggestions {
    margin: 0 $grid-gutter-width / 3 $grid-gutter-width * 2 $grid-gutter-width / 3;

    &--heading {
      text-align: left;
      color: var(--primary);
      font-size: 22px;
      margin-bottom: $grid-gutter-width / 3;
    }
    &--panel {
      // No vertical padding so the hover/selected background spans the full
      // panel height; separators are inset via ::before.
      &.panel {
        padding: 0 $grid-gutter-width / 2;
        filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.10));
      }
      swiper-slide {
        position: relative;
      }
      // Doubled class to beat swiper.vue's scoped `.swiper__button` rule
      .swiper__button.swiper__button {
        border: solid 1px $gray-300;
        
        &--disabled {
          cursor: default;

          .vb-icon path {
            fill: $gray-300;
          }
        }
      }
      swiper-slide:not(:first-child)::before {
        content: '';
        position: absolute;
        top: 25%;
        bottom: 25%;
        left: 0;
        border-left: 1px solid $gray-300;
      }
      // First visible slide after a scroll: no separator on the panel edge
      swiper-slide:not(.swiper-slide-visible)
        + swiper-slide.swiper-slide-visible::before {
        display: none;
      }
    }
    &--date {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: $grid-gutter-width $grid-gutter-width / 3;
      background: none;
      border: 0;
      border-bottom: 2px solid transparent;
      color: inherit;
      cursor: pointer;
      transition: $transition-base;

      &:hover,
      &--selected {
        background-color: $selected;
        border-bottom-color: var(--primary);
      }
    }
    &--range {
      color: $gray-700;
      font-weight: 600;
      line-height: 1.25;
    }
    &--price {
      color: $gray-600;
      font-size: $font-size-md;
      line-height: 1.5;
      span {
        color: var(--primary);
        @include font-primary-bold;
      }
    }
  }
</style>
